import { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Card, Modal, SUB_CONFIRM_LAYER, buttonClassName, useConfirmDialog } from '@safetyhub/ui';
import { adminApi, WorkClassification, WorkClassificationInput, WcSifImportProgress } from '../lib/api';
import { ModalCancelButton } from '../components/ModalCancelButton';
import { apiErrorMessage } from '../lib/apiErrorMessage';
import { useToastStore } from '../stores/toast';

// 소스 라벨
const SOURCE_LABELS: Record<string, string> = {
  SIF: 'SIF 공식',
  AI: 'AI 생성',
  USER: '사용자 입력',
};

// 소스별 배지 색상
const SOURCE_BADGE_COLORS: Record<string, string> = {
  SIF: 'bg-blue-100 text-blue-800',
  AI: 'bg-purple-100 text-purple-800',
  USER: 'bg-gray-100 text-gray-800',
};

// SIF Import 단계
type SifImportStep = 'select' | 'importing' | 'result';

// SIF Import 결과 타입
interface SifImportResult {
  success: boolean;
  created: number;
  updated: number;
  skipped: number;
  error?: string;
}

export default function WorkClassifications() {
  const queryClient = useQueryClient();
  const { showToast } = useToastStore();
  const [confirm, confirmDialog] = useConfirmDialog();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({
    industryType: '',
    category: '',
    source: '' as '' | 'SIF' | 'AI' | 'USER',
    isOfficial: undefined as boolean | undefined,
    search: '',
  });
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingItem, setEditingItem] = useState<WorkClassification | null>(null);
  const [selectedItem, setSelectedItem] = useState<WorkClassification | null>(null);
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetPassword, setResetPassword] = useState('');
  const resetPasswordRef = useRef<HTMLInputElement>(null);
  const bulkDeleteCancelRef = useRef<HTMLButtonElement>(null);
  /**
   * 선택 — id → **표시 라벨**. 배열(`string[]`)이 아니라 라벨을 함께 드는 것이 요점이다:
   * 선택이 페이지를 넘어 살아남으므로, 삭제 확인에 보일 목록을 현재 페이지 `items`에서 뽑으면
   * 다른 페이지에서 고른 대상이 통째로 빠진다. 삭제는 hard delete(서버
   * `admin-work-classification.service.ts`의 `deleteMany` · `deletedAt` 칼럼이 없다)라 그 누락이
   * 되돌릴 수 없다.
   */
  const [selectedItems, setSelectedItems] = useState<Map<string, string>>(new Map());
  const [bulkDeleteModal, setBulkDeleteModal] = useState(false);

  // SIF Import 관련 상태
  const [showSifModal, setShowSifModal] = useState(false);
  const [sifImportStep, setSifImportStep] = useState<SifImportStep>('select');
  const [sifImportResult, setSifImportResult] = useState<SifImportResult | null>(null);
  const [importProgress, setImportProgress] = useState<{
    percent: number;
    phase: string;
    processed: number;
    total: number;
    created: number;
    updated: number;
    skipped: number;
    embeddingProcessed: number;
    embeddingTotal: number;
  } | null>(null);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // 목록 조회
  const { data: listData, isLoading } = useQuery({
    queryKey: ['workClassifications', page, filters],
    queryFn: () =>
      adminApi.getWorkClassifications({
        page,
        limit: 20,
        industryType: filters.industryType || undefined,
        category: filters.category || undefined,
        source: filters.source || undefined,
        isOfficial: filters.isOfficial,
        search: filters.search || undefined,
        sortBy: 'category',
        sortOrder: 'asc',
      }),
  });

  // 통계 조회
  const { data: statsData } = useQuery({
    queryKey: ['workClassificationStats'],
    queryFn: () => adminApi.getWorkClassificationStats(),
  });

  // 카테고리 목록 조회
  const { data: categoriesData } = useQuery({
    queryKey: ['workClassificationCategories', filters.industryType],
    queryFn: () => adminApi.getWorkClassificationCategories(filters.industryType || undefined),
  });

  // 저장 mutation
  const saveMutation = useMutation({
    mutationFn: (data: { id?: string; input: WorkClassificationInput }) =>
      data.id
        ? adminApi.updateWorkClassification(data.id, data.input)
        : adminApi.createWorkClassification(data.input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workClassifications'] });
      queryClient.invalidateQueries({ queryKey: ['workClassificationStats'] });
      queryClient.invalidateQueries({ queryKey: ['workClassificationCategories'] });
      setShowEditModal(false);
      setEditingItem(null);
    },
    // 전역 mutation 기본 onError가 없어(이중 토스트 방지로 두지 않는다) 거부 사유를 화면이 띄운다.
    onError: (err) => showToast(apiErrorMessage(err, '저장에 실패했습니다.'), 'error'),
  });

  // 삭제 mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => adminApi.deleteWorkClassification(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workClassifications'] });
      queryClient.invalidateQueries({ queryKey: ['workClassificationStats'] });
      setSelectedItem(null);
    },
    onError: (err) => showToast(apiErrorMessage(err, '삭제에 실패했습니다.'), 'error'),
  });

  // 벌크 삭제 mutation
  const bulkDeleteMutation = useMutation({
    mutationFn: (ids: string[]) => adminApi.deleteWorkClassifications(ids),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workClassifications'] });
      queryClient.invalidateQueries({ queryKey: ['workClassificationStats'] });
      setSelectedItems(new Map());
      setBulkDeleteModal(false);
    },
    onError: (err) => showToast(apiErrorMessage(err, '일괄 삭제에 실패했습니다.'), 'error'),
  });

  // 진행률 폴링 시작
  const startProgressPolling = (jobId: string) => {
    if (pollingRef.current) clearInterval(pollingRef.current);
    pollingRef.current = setInterval(async () => {
      try {
        const res = await adminApi.getWcSifImportProgress(jobId);
        if (!res.data) return;

        const p = res.data;
        setImportProgress({
          percent: p.percent,
          phase: p.phase,
          processed: p.processed,
          total: p.total,
          created: p.created,
          updated: p.updated,
          skipped: p.skipped,
          embeddingProcessed: p.embeddingProcessed,
          embeddingTotal: p.embeddingTotal,
        });

        if (p.status === 'completed') {
          if (pollingRef.current) clearInterval(pollingRef.current);
          pollingRef.current = null;
          queryClient.invalidateQueries({ queryKey: ['workClassifications'] });
          queryClient.invalidateQueries({ queryKey: ['workClassificationStats'] });
          queryClient.invalidateQueries({ queryKey: ['workClassificationCategories'] });
          setSifImportResult({
            success: true,
            created: p.result?.created || p.created,
            updated: p.result?.updated || p.updated,
            skipped: p.result?.skipped || p.skipped,
          });
          setSifImportStep('result');
        } else if (p.status === 'failed') {
          if (pollingRef.current) clearInterval(pollingRef.current);
          pollingRef.current = null;
          setSifImportResult({
            success: false,
            created: 0,
            updated: 0,
            skipped: 0,
            error: p.error || 'Import 실패',
          });
          setSifImportStep('result');
        }
      } catch {
        // 네트워크 오류 시 폴링 계속 시도
      }
    }, 1000);
  };

  // 페이지 로드 시 활성 Import 복구
  useEffect(() => {
    (async () => {
      try {
        const res = await adminApi.getActiveWcSifImport();
        if (res.data) {
          const p = res.data as WcSifImportProgress;
          setShowSifModal(true);
          setSifImportStep('importing');
          setImportProgress({
            percent: p.percent,
            phase: p.phase,
            processed: p.processed,
            total: p.total,
            created: p.created,
            updated: p.updated,
            skipped: p.skipped,
            embeddingProcessed: p.embeddingProcessed,
            embeddingTotal: p.embeddingTotal,
          });
          if (p.jobId) startProgressPolling(p.jobId);
        }
      } catch {
        // ignore
      }
    })();

    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, []);

  // 초기화 mutation
  const resetMutation = useMutation({
    mutationFn: (password: string) => adminApi.resetWorkClassifications(password),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['workClassifications'] });
      queryClient.invalidateQueries({ queryKey: ['workClassificationStats'] });
      queryClient.invalidateQueries({ queryKey: ['workClassificationCategories'] });
      setShowResetModal(false);
      setResetPassword('');
      alert(`${data.data?.deletedCount || 0}건의 공종-작업 분류가 삭제되었습니다.`);
    },
    onError: (error: Error) => {
      alert(error.message || '초기화에 실패했습니다.');
    },
  });

  const items = listData?.data || [];
  const meta = listData?.meta;
  const totalPages = meta ? Math.ceil(meta.total / (meta.limit || 20)) : 1;
  const stats = statsData?.data;
  const categories = categoriesData?.data || undefined;

  /**
   * 필터가 바뀌면 선택을 비운다 — 「다른 집합을 보게 됐다」이기 때문이다.
   *
   * ⚠️ deps는 `filters`의 **다섯 키 전부**이고 `page`는 **뺀 것**이다. `page`는 조회 집합의
   * «멤버십»이 아니라 창만 바꾸므로, 넣으면 「여러 페이지에 걸쳐 고르고 한 번에 삭제」가
   * 불가능해진다.
   * ⚠️ **검색어가 deps에 드는지는 「그 값이 조회 키에 실려 입력마다 재조회하나」가 정한다 —
   * 어느 화면이 어떻게 했나가 아니라 그 조건으로 판정하라.** 여기서는 `search`가
   * `queryKey: ['workClassifications', page, filters]`에 그대로 실리고 디바운스가 없어 **타자마다
   * 서버 조회가 다시 돌아 보는 집합이 실제로 갈린다** ⇒ 멤버십 필터와 같은 축이므로 **든다**.
   * 거울상도 참이다: **조회가 submit에서만 도는 화면이면 빼야 한다** — 넣으면 목록은 그대로인데
   * 타자만 쳐도 선택이 사라지고, 그쪽에서는 그것이 결함이다. 그래서 화면마다 deps가 다른 것은
   * **불일치가 아니라 같은 규칙의 두 결과**다. 「통일」하지 말 것 — 통일하려면 먼저 그 화면의
   * 조회가 «언제» 도는지 보라.
   * ⚠️ 이 effect가 아니라 `handleFilterChange` 한 줄로도 오늘은 같다(`setFilters` 호출부가 그
   * 하나뿐이다). effect로 둔 것은 **다음 사람이 두 번째 `setFilters` 호출부를 더해도 축이 닫혀
   * 있게** 하기 위해서다.
   */
  useEffect(() => {
    setSelectedItems(new Map());
  }, [
    filters.industryType,
    filters.category,
    filters.source,
    filters.isOfficial,
    filters.search,
  ]);

  const handleFilterChange = (key: string, value: string | boolean | undefined) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const handleEdit = (item: WorkClassification) => {
    setEditingItem(item);
    setSelectedItem(null);
    setShowEditModal(true);
  };

  const handleRowClick = (item: WorkClassification) => {
    setSelectedItem(item);
  };

  const handleCreate = () => {
    setEditingItem(null);
    setShowEditModal(true);
  };

  // 상세 모달(z-[70]) 안의 「삭제」에서만 부르므로 확인은 한 층 위(`SUB_CONFIRM_LAYER`).
  const handleDelete = async (id: string) => {
    if (
      await confirm({
        title: '공종-작업 분류 삭제',
        body: '이 항목을 삭제하시겠습니까?',
        confirmLabel: '삭제',
        danger: true,
        ...SUB_CONFIRM_LAYER,
      })
    ) {
      deleteMutation.mutate(id);
    }
  };

  const handleBulkDelete = () => {
    if (selectedItems.size === 0) return;
    setBulkDeleteModal(true);
  };

  const confirmBulkDelete = () => {
    if (selectedItems.size === 0) return;
    bulkDeleteMutation.mutate(Array.from(selectedItems.keys()));
  };

  /**
   * 삭제 확인 모달에 보일 한 줄. 표의 식별 칸(산업유형 · 공종 · 작업명 · 소분류)을 그대로 잇는다.
   */
  const itemLabel = (item: WorkClassification): string =>
    [
      [item.industryType, item.category].filter(Boolean).join('/'),
      item.subCategory,
      item.detailCategory,
    ]
      .filter(Boolean)
      .join(' · ') || item.id;

  const toggleSelect = (item: WorkClassification) => {
    const next = new Map(selectedItems);
    if (next.has(item.id)) {
      next.delete(item.id);
    } else {
      next.set(item.id, itemLabel(item));
    }
    setSelectedItems(next);
  };

  /**
   * 「이 페이지가 전부 선택됐나」를 «멤버십»으로 판정한다 — 수로 판정하면 안 된다.
   * 선택이 페이지를 넘어 살아남으므로 `size === items.length`는 **다른 페이지에서 고른 20개**를
   * 「이 페이지 전체 선택됨」으로 읽고, 그 상태에서 누르면 선택이 통째로 비워진다.
   */
  const allOnPageSelected = items.length > 0 && items.every((item) => selectedItems.has(item.id));

  /**
   * 선택했지만 «현재 페이지에 없는» 개수. 선택 바가 이것을 말해 주지 않으면 운영자는
   * 「화면에 보이는 것만 지워진다」로 읽는다 — 그 오독이 되돌릴 수 없는 삭제로 이어진다.
   */
  const offPageSelectedCount = Array.from(selectedItems.keys()).filter(
    (id) => !items.some((item) => item.id === id),
  ).length;

  /** 현재 «페이지»의 전체 선택/해제 — 다른 페이지에서 고른 것은 건드리지 않는다. */
  const toggleSelectAll = () => {
    const next = new Map(selectedItems);
    if (allOnPageSelected) {
      items.forEach((item) => next.delete(item.id));
    } else {
      items.forEach((item) => next.set(item.id, itemLabel(item)));
    }
    setSelectedItems(next);
  };

  // SIF Import 파일 선택 핸들러
  const handleImportSif = async (file: File) => {
    setSifImportStep('importing');
    setImportProgress(null);

    try {
      const response = await adminApi.importSifWorkClassifications(file);
      if (response.data?.jobId) {
        startProgressPolling(response.data.jobId);
      }
    } catch (error: any) {
      setSifImportResult({
        success: false,
        created: 0,
        updated: 0,
        skipped: 0,
        error: error.message || 'SIF 가져오기에 실패했습니다.',
      });
      setSifImportStep('result');
    }
  };

  // SIF 모달 닫기
  const closeSifModal = () => {
    if (sifImportStep === 'importing') return; // 진행 중 닫기 방지
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
    setShowSifModal(false);
    setSifImportStep('select');
    setSifImportResult(null);
    setImportProgress(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-6">
      {/* 헤더 */}
      <div className="flex items-center justify-between gap-4">
        <h1 className="text-2xl font-bold text-gray-900 whitespace-nowrap">공종-작업 분류 관리</h1>
        <div className="flex items-center gap-2 flex-shrink-0">
          <Button
            variant="danger"
            onClick={() => setShowResetModal(true)}
            className="whitespace-nowrap"
          >
            초기화
          </Button>
          <Button
            variant="success"
            onClick={() => setShowSifModal(true)}
            className="whitespace-nowrap"
          >
            SIF에서 가져오기
          </Button>
          <Button variant="primary" onClick={handleCreate} className="whitespace-nowrap">
            + 추가
          </Button>
        </div>
      </div>

      {/* 통계 카드 */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card radius="lg">
            <div className="text-2xl font-bold text-gray-900">{stats.total}</div>
            <div className="text-sm text-gray-500">전체</div>
          </Card>
          <Card radius="lg">
            <div className="text-2xl font-bold text-brand-600">{stats.official}</div>
            <div className="text-sm text-gray-500">공식 데이터</div>
          </Card>
          <Card radius="lg">
            <div className="text-2xl font-bold text-purple-600">{stats.bySource?.SIF || 0}</div>
            <div className="text-sm text-gray-500">SIF</div>
          </Card>
          <Card radius="lg">
            <div className="text-2xl font-bold text-gray-600">{stats.bySource?.USER || 0}</div>
            <div className="text-sm text-gray-500">사용자</div>
          </Card>
        </div>
      )}

      {/* 필터 */}
      <Card radius="lg">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">산업유형</label>
            <select
              value={filters.industryType}
              onChange={(e) => handleFilterChange('industryType', e.target.value)}
              className="w-full border rounded-control px-3 py-2"
            >
              <option value="">전체</option>
              {categories?.industryTypes.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">공종(대분류)</label>
            <select
              value={filters.category}
              onChange={(e) => handleFilterChange('category', e.target.value)}
              className="w-full border rounded-control px-3 py-2"
            >
              <option value="">전체</option>
              {categories?.categories.map((cat) => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">소스</label>
            <select
              value={filters.source}
              onChange={(e) => handleFilterChange('source', e.target.value as '' | 'SIF' | 'AI' | 'USER')}
              className="w-full border rounded-control px-3 py-2"
            >
              <option value="">전체</option>
              <option value="SIF">SIF 공식</option>
              <option value="AI">AI 생성</option>
              <option value="USER">사용자 입력</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">공식여부</label>
            <select
              value={filters.isOfficial === undefined ? '' : String(filters.isOfficial)}
              onChange={(e) =>
                handleFilterChange(
                  'isOfficial',
                  e.target.value === '' ? undefined : e.target.value === 'true'
                )
              }
              className="w-full border rounded-control px-3 py-2"
            >
              <option value="">전체</option>
              <option value="true">공식</option>
              <option value="false">비공식</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">검색</label>
            <input
              type="text"
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              placeholder="공종, 작업명 검색"
              className="w-full border rounded-control px-3 py-2"
            />
          </div>
        </div>
      </Card>

      {/* 벌크 액션 */}
      {selectedItems.size > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-card p-4 flex items-center justify-between">
          <span className="text-yellow-800">
            {selectedItems.size}개 선택됨
            {offPageSelectedCount > 0 && (
              <span className="ml-1 text-yellow-700">
                (이 페이지에 없는 {offPageSelectedCount}개 포함)
              </span>
            )}
          </span>
          <Button
            variant="danger"
            onClick={handleBulkDelete}
            disabled={bulkDeleteMutation.isPending}
          >
            선택 삭제
          </Button>
        </div>
      )}

      {/* 테이블 */}
      <Card padding="none" radius="lg" className="overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-gray-500">로딩 중...</div>
        ) : items.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            데이터가 없습니다. "SIF에서 가져오기" 버튼을 클릭하여 기본 데이터를 가져오세요.
          </div>
        ) : (
          <div className="overflow-x-auto">
          <table className="w-full min-w-[900px] divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={allOnPageSelected}
                    onChange={toggleSelectAll}
                    className="rounded"
                  />
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  산업유형
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  공종(대분류)
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  작업명(중분류)
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  소분류
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  소스
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  상태
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  사용횟수
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {items.map((item) => (
                <tr
                  key={item.id}
                  onClick={() => handleRowClick(item)}
                  className="hover:bg-brand-50 cursor-pointer transition-colors"
                >
                  <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selectedItems.has(item.id)}
                      onChange={() => toggleSelect(item)}
                      className="rounded"
                    />
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">{item.industryType}</td>
                  <td className="px-4 py-3 text-sm text-gray-900 font-medium whitespace-nowrap">{item.category}</td>
                  <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">{item.subCategory}</td>
                  <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">{item.detailCategory || '-'}</td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        SOURCE_BADGE_COLORS[item.source] || 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {SOURCE_LABELS[item.source] || item.source}
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    {item.isOfficial && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-brand-100 text-brand-800">
                        공식
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">{item.usageCount}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}

        {/* 페이지네이션 */}
        {meta && totalPages > 1 && (
          <div className="bg-white px-4 py-3 border-t border-gray-200 flex items-center justify-between">
            <div className="text-sm text-gray-700">
              전체 {meta.total}건 중 {(page - 1) * 20 + 1} - {Math.min(page * 20, meta.total)}건
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-3 py-1 border rounded disabled:opacity-50"
              >
                이전
              </button>
              <span className="text-sm">
                {page} / {totalPages}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="px-3 py-1 border rounded disabled:opacity-50"
              >
                다음
              </button>
            </div>
          </div>
        )}
      </Card>

      {/* 상세 보기 모달 */}
      {selectedItem && (
        <DetailModal
          item={selectedItem}
          onClose={() => setSelectedItem(null)}
          onEdit={() => handleEdit(selectedItem)}
          onDelete={() => handleDelete(selectedItem.id)}
          isDeleting={deleteMutation.isPending}
        />
      )}
      {confirmDialog}

      {/* 생성/수정 모달 */}
      {showEditModal && (
        <EditModal
          item={editingItem}
          onClose={() => {
            setShowEditModal(false);
            setEditingItem(null);
          }}
          onSave={(input) => {
            saveMutation.mutate({ id: editingItem?.id, input });
          }}
          isLoading={saveMutation.isPending}
          categories={categories}
        />
      )}

      {/* SIF 가져오기 모달 */}
      {/* 가져오는 중에는 닫는 길(오버레이·Esc·✕) 전부를 막는다 — 종전에도 그 단계에서 바깥 클릭·✕가
          꺼져 있었다(Esc는 종전엔 아예 없었다). 헤더 고정 + 본문 스크롤은 base `bodyScroll`이 맡는다
          (종전 헤더의 `sticky top-0`과 같은 결과). */}
      {showSifModal && (
        <Modal
          open
          onClose={closeSifModal}
          title="SIF에서 가져오기"
          size="lg"
          closeOnOverlay={sifImportStep !== 'importing'}
          closeOnEsc={sifImportStep !== 'importing'}
          showCloseButton={sifImportStep !== 'importing'}
          closeLocked={sifImportStep === 'importing'}
        >
            <div>
              {/* Step 1: 파일 선택 */}
              {sifImportStep === 'select' && (
                <div className="text-center py-8">
                  <div className="mb-6">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                      </svg>
                    </div>
                    <h3 className="text-lg font-medium mb-2">SIF 엑셀 파일 선택</h3>
                    <p className="text-gray-500 text-sm mb-4">
                      산업재해 고위험요인(SIF) 활용자료 엑셀 파일을 선택하세요.
                    </p>
                    <p className="text-gray-400 text-xs">
                      파일에서 공종-작업 분류 데이터를 추출합니다.
                    </p>
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleImportSif(file);
                    }}
                    className="hidden"
                  />
                  <Button
                    variant="success"
                    size="lg"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    파일 선택
                  </Button>
                </div>
              )}

              {/* Step 2: 가져오기 진행 중 */}
              {sifImportStep === 'importing' && (
                <div className="py-8">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-green-600 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  </div>
                  <h3 className="text-lg font-medium mb-2 text-center">
                    {importProgress?.phase === 'embedding' ? '임베딩 생성 중...' : 'SIF에서 가져오는 중...'}
                  </h3>
                  {importProgress && (
                    <div className="mt-4">
                      {/* 현재 단계 표시 */}
                      <div className="flex justify-between text-sm text-gray-600 mb-1">
                        <span>
                          {importProgress.phase === 'embedding'
                            ? `임베딩 ${importProgress.embeddingProcessed} / ${importProgress.embeddingTotal}`
                            : `가져오기 ${importProgress.processed} / ${importProgress.total}`
                          }
                        </span>
                        <span>{importProgress.percent}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-green-500 h-2 rounded-full transition-all"
                          style={{ width: `${importProgress.percent}%` }}
                        />
                      </div>
                      {/* 2단계 표시 */}
                      <div className="flex items-center gap-2 mt-3 text-xs text-gray-500">
                        <span className={importProgress.phase === 'importing' ? 'text-green-600 font-medium' : 'text-gray-400'}>
                          1. 가져오기
                        </span>
                        <span className="text-gray-300">→</span>
                        <span className={importProgress.phase === 'embedding' ? 'text-green-600 font-medium' : 'text-gray-400'}>
                          2. 임베딩
                        </span>
                      </div>
                      {/* 결과 카운터 */}
                      <div className="grid grid-cols-3 gap-2 mt-3 text-center text-xs text-gray-500">
                        <div>
                          <div className="text-lg font-semibold text-green-600">{importProgress.created}</div>
                          생성
                        </div>
                        <div>
                          <div className="text-lg font-semibold text-blue-600">{importProgress.updated}</div>
                          업데이트
                        </div>
                        <div>
                          <div className="text-lg font-semibold text-gray-600">{importProgress.skipped}</div>
                          건너뜀
                        </div>
                      </div>
                    </div>
                  )}
                  {!importProgress && (
                    <p className="text-gray-500 text-sm text-center">잠시만 기다려주세요.</p>
                  )}
                </div>
              )}

              {/* Step 3: 결과 */}
              {sifImportStep === 'result' && sifImportResult && (
                <>
                  <div className="flex items-center gap-3 mb-6">
                    {sifImportResult.success ? (
                      <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                        <span className="text-2xl text-green-600">✓</span>
                      </div>
                    ) : (
                      <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                        <span className="text-2xl text-red-600">!</span>
                      </div>
                    )}
                    <div>
                      <h3 className="text-lg font-semibold">
                        {sifImportResult.success ? '가져오기 완료' : '가져오기 실패'}
                      </h3>
                      {sifImportResult.success && (
                        <p className="text-gray-500">
                          총 {sifImportResult.created + sifImportResult.updated + sifImportResult.skipped}건 처리됨
                        </p>
                      )}
                    </div>
                  </div>

                  {sifImportResult.success ? (
                    <div className="grid grid-cols-3 gap-4 mb-6">
                      <div className="bg-green-50 rounded-card p-4 text-center">
                        <div className="text-2xl font-bold text-green-600">
                          {sifImportResult.created}
                        </div>
                        <div className="text-sm text-gray-500">생성됨</div>
                      </div>
                      <div className="bg-blue-50 rounded-card p-4 text-center">
                        <div className="text-2xl font-bold text-blue-600">
                          {sifImportResult.updated}
                        </div>
                        <div className="text-sm text-gray-500">업데이트됨</div>
                      </div>
                      <div className="bg-yellow-50 rounded-card p-4 text-center">
                        <div className="text-2xl font-bold text-yellow-600">
                          {sifImportResult.skipped}
                        </div>
                        <div className="text-sm text-gray-500">건너뜀</div>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-red-50 rounded-card p-4 mb-6">
                      <p className="text-red-700">{sifImportResult.error}</p>
                    </div>
                  )}

                  <div className="flex justify-end gap-3 pt-4 border-t">
                    <button
                      onClick={() => {
                        setSifImportStep('select');
                        setSifImportResult(null);
                        if (fileInputRef.current) {
                          fileInputRef.current.value = '';
                        }
                      }}
                      className="px-4 py-2 border rounded-control hover:bg-gray-50"
                    >
                      새 파일 선택
                    </button>
                    <Button variant="primary" onClick={closeSifModal}>
                      닫기
                    </Button>
                  </div>
                </>
              )}
            </div>
        </Modal>
      )}

      {/*
        일괄 삭제 확인 — 「수」가 아니라 «대상»을 보인다.
        선택이 페이지를 넘어 살아남으므로 목록을 `items`(현재 페이지)에서 뽑으면 일부가 빠진다.
        `selectedItems`가 선택 시점에 라벨을 함께 들고 있어 그 축이 닫힌다.
      */}
      {/* danger 규약 — 되돌릴 수 없는 삭제라 첫 포커스는 「취소」(Enter 오조작 방지 — `Button`은 ref를
          넘기지 않아 취소만 같은 정본 클래스의 네이티브 버튼이다). 삭제 중에는 닫는 길 전부를 막는다. */}
      {bulkDeleteModal && (
        <Modal
          open
          onClose={() => setBulkDeleteModal(false)}
          title="선택 항목 삭제"
          size="xl"
          initialFocusRef={bulkDeleteCancelRef}
          closeOnOverlay={!bulkDeleteMutation.isPending}
          closeOnEsc={!bulkDeleteMutation.isPending}
          showCloseButton={!bulkDeleteMutation.isPending}
          closeLocked={bulkDeleteMutation.isPending}
          footer={
            <>
              <button
                ref={bulkDeleteCancelRef}
                type="button"
                className={buttonClassName('secondary')}
                onClick={() => setBulkDeleteModal(false)}
                disabled={bulkDeleteMutation.isPending}
              >
                취소
              </button>
              <Button
                variant="danger"
                onClick={confirmBulkDelete}
                disabled={bulkDeleteMutation.isPending}
              >
                {bulkDeleteMutation.isPending ? '삭제 중...' : `${selectedItems.size}건 삭제`}
              </Button>
            </>
          }
        >
            <div>
              <div className="bg-red-50 border border-red-200 rounded-card p-4 mb-4">
                <p className="text-sm text-red-700 font-medium mb-2">
                  이 작업은 되돌릴 수 없습니다. 삭제 기록도 남지 않습니다.
                </p>
                <ul className="text-sm text-red-600 space-y-1">
                  <li>- 공종-작업 분류 {selectedItems.size}건이 삭제됩니다</li>
                  {offPageSelectedCount > 0 && (
                    <li>- 그중 {offPageSelectedCount}건은 다른 페이지에서 선택한 항목입니다</li>
                  )}
                </ul>
              </div>

              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">삭제 대상</p>
                <ul className="max-h-64 overflow-y-auto border rounded-card divide-y divide-gray-100">
                  {Array.from(selectedItems.entries()).map(([id, label]) => (
                    <li key={id} className="px-3 py-2 text-sm text-gray-700 truncate" title={label}>
                      {label}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
        </Modal>
      )}

      {/* 초기화 확인 모달 */}
      {/* 되돌릴 수 없는 초기화라 비밀번호를 받는다 — 첫 포커스는 그 입력이다(`autoFocus`는 base가 열림
          직후 패널로 포커스를 옮기며 덮으므로 `initialFocusRef`). 초기화 중에는 닫는 길 전부를 막는다. */}
      {showResetModal && (
        <Modal
          open
          onClose={() => { setShowResetModal(false); setResetPassword(''); }}
          title="전체 초기화"
          size="md"
          initialFocusRef={resetPasswordRef}
          closeOnOverlay={!resetMutation.isPending}
          closeOnEsc={!resetMutation.isPending}
          showCloseButton={!resetMutation.isPending}
          closeLocked={resetMutation.isPending}
          footer={
            <>
              <Button
                variant="secondary"
                onClick={() => {
                  setShowResetModal(false);
                  setResetPassword('');
                }}
                disabled={resetMutation.isPending}
              >
                취소
              </Button>
              <Button
                variant="danger"
                onClick={() => resetMutation.mutate(resetPassword)}
                disabled={!resetPassword || resetMutation.isPending}
              >
                {resetMutation.isPending ? '삭제 중...' : '초기화'}
              </Button>
            </>
          }
        >
          <p className="text-red-600 font-medium mb-4">
            모든 공종-작업 분류 데이터가 삭제됩니다. 계속하려면 비밀번호를 입력하세요.
          </p>
          <input
            ref={resetPasswordRef}
            type="password"
            value={resetPassword}
            onChange={(e) => setResetPassword(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && resetPassword && !resetMutation.isPending) {
                resetMutation.mutate(resetPassword);
              }
            }}
            placeholder="비밀번호"
            className="w-full border rounded-control px-3 py-2"
          />
        </Modal>
      )}
    </div>
  );
}

// 생성/수정 모달 컴포넌트
interface EditModalProps {
  item: WorkClassification | null;
  onClose: () => void;
  onSave: (input: WorkClassificationInput) => void;
  isLoading: boolean;
  categories?: { industryTypes: string[]; categories: string[] };
}

function EditModal({ item, onClose, onSave, isLoading, categories }: EditModalProps) {
  const isEditing = !!item;
  const [form, setForm] = useState<WorkClassificationInput>({
    industryType: item?.industryType || '건설업',
    category: item?.category || '',
    subCategory: item?.subCategory || '',
    detailCategory: item?.detailCategory || undefined,
    source: item?.source || 'USER',
    isOfficial: item?.isOfficial || false,
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.industryType || !form.category || !form.subCategory) {
      alert('산업유형, 공종, 작업명은 필수입니다.');
      return;
    }
    onSave(form);
  };

  // 미저장 변경 보호 — 첫 렌더의 폼을 스냅샷으로 두고 비교한다. 종전엔 바깥 클릭·Esc로 닫히지 않아
  // 입력을 잃을 길이 취소뿐이었는데, base로 옮기며 그 둘이 생겨 dirty 확인을 함께 건다.
  const [initialSnapshot] = useState(() => JSON.stringify(form));
  const isDirty = JSON.stringify(form) !== initialSnapshot;

  // 저장 중에는 닫는 길(오버레이·Esc·✕) 전부를 막는다.
  return (
    <Modal
      open
      onClose={onClose}
      title={isEditing ? '공종-작업 분류 수정' : '공종-작업 분류 추가'}
      size="lg"
      isDirty={isDirty}
      closeOnOverlay={!isLoading}
      closeOnEsc={!isLoading}
      showCloseButton={!isLoading}
      closeLocked={isLoading}
    >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              산업유형 <span className="text-red-500">*</span>
            </label>
            <select
              value={form.industryType}
              onChange={(e) => setForm((f) => ({ ...f, industryType: e.target.value }))}
              className="w-full border rounded-control px-3 py-2"
            >
              <option value="건설업">건설업</option>
              <option value="제조업">제조업</option>
              <option value="기타의사업">기타의사업</option>
              <option value="운수창고통신업">운수창고통신업</option>
              <option value="임업">임업</option>
              <option value="농업">농업</option>
              <option value="광업">광업</option>
              <option value="전기가스증기수도사업">전기가스증기수도사업</option>
              {categories?.industryTypes
                .filter((t) => !['건설업', '제조업', '기타의사업', '운수창고통신업', '임업', '농업', '광업', '전기가스증기수도사업'].includes(t))
                .map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              공종(대분류) <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={form.category}
              onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
              placeholder="예: 토공사"
              className="w-full border rounded-control px-3 py-2"
              list="category-list"
            />
            <datalist id="category-list">
              {categories?.categories.map((cat) => (
                <option key={cat} value={cat} />
              ))}
            </datalist>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              작업명(중분류) <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={form.subCategory}
              onChange={(e) => setForm((f) => ({ ...f, subCategory: e.target.value }))}
              placeholder="예: 굴착작업"
              className="w-full border rounded-control px-3 py-2"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">소분류</label>
            <input
              type="text"
              value={form.detailCategory || ''}
              onChange={(e) =>
                setForm((f) => ({ ...f, detailCategory: e.target.value || undefined }))
              }
              placeholder="선택사항"
              className="w-full border rounded-control px-3 py-2"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">소스</label>
            <select
              value={form.source}
              onChange={(e) =>
                setForm((f) => ({ ...f, source: e.target.value as 'SIF' | 'AI' | 'USER' }))
              }
              className="w-full border rounded-control px-3 py-2"
            >
              <option value="USER">사용자 입력</option>
              <option value="AI">AI 생성</option>
              <option value="SIF">SIF 공식</option>
            </select>
          </div>

          <div className="flex items-center gap-6">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={form.isOfficial}
                onChange={(e) => setForm((f) => ({ ...f, isOfficial: e.target.checked }))}
                className="rounded"
              />
              <span className="text-sm text-gray-700">공식 데이터</span>
            </label>
          </div>

          <div className="flex justify-end gap-2 pt-4">
            {/* 취소는 base 닫기 요청으로 수렴해 dirty 확인을 우회하지 않는다 */}
            <ModalCancelButton onCancel={onClose} disabled={isLoading} />
            <Button type="submit" variant="primary" disabled={isLoading}>
              {isLoading ? '저장 중...' : '저장'}
            </Button>
          </div>
        </form>
    </Modal>
  );
}

// 상세 보기 모달 컴포넌트
interface DetailModalProps {
  item: WorkClassification;
  onClose: () => void;
  onEdit: () => void;
  onDelete: () => void;
  isDeleting: boolean;
}

function DetailModal({ item, onClose, onEdit, onDelete, isDeleting }: DetailModalProps) {
  // 헤더·푸터 고정 + 본문 스크롤은 base `bodyScroll`이 맡는다(종전 `sticky top-0`/`bottom-0`과 같은 결과).
  // 삭제 중에는 닫는 길을 막는다. 푸터는 base가 `justify-end`라 삭제를 `mr-auto`로 왼쪽에 둔다.
  return (
    <Modal
      open
      onClose={onClose}
      title="공종-작업 분류 상세"
      size="xl"
      closeOnOverlay={!isDeleting}
      closeOnEsc={!isDeleting}
      showCloseButton={!isDeleting}
      closeLocked={isDeleting}
      footer={
        <>
          <button
            onClick={onDelete}
            disabled={isDeleting}
            className="mr-auto px-4 py-2 text-red-600 hover:bg-red-50 rounded-control disabled:opacity-50"
          >
            {isDeleting ? '삭제 중...' : '삭제'}
          </button>
          <Button variant="secondary" onClick={onClose} disabled={isDeleting}>
            닫기
          </Button>
          <Button variant="primary" onClick={onEdit} disabled={isDeleting}>
            수정
          </Button>
        </>
      }
    >
        <div className="space-y-6">
          {/* 분류 정보 */}
          <section>
            <h3 className="text-sm font-medium text-gray-500 mb-3">분류 정보</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-xs text-gray-400 mb-1">산업유형</div>
                <div className="font-medium">{item.industryType}</div>
              </div>
              <div>
                <div className="text-xs text-gray-400 mb-1">공종(대분류)</div>
                <div className="font-medium">{item.category}</div>
              </div>
              <div>
                <div className="text-xs text-gray-400 mb-1">작업명(중분류)</div>
                <div className="font-medium">{item.subCategory}</div>
              </div>
              <div>
                <div className="text-xs text-gray-400 mb-1">소분류</div>
                <div className="font-medium">
                  {item.detailCategory || <span className="text-gray-300">-</span>}
                </div>
              </div>
            </div>
          </section>

          {/* 메타 정보 */}
          <section>
            <h3 className="text-sm font-medium text-gray-500 mb-3">메타 정보</h3>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <div className="text-xs text-gray-400 mb-1">데이터 소스</div>
                <span
                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    SOURCE_BADGE_COLORS[item.source] || 'bg-gray-100 text-gray-800'
                  }`}
                >
                  {SOURCE_LABELS[item.source] || item.source}
                </span>
              </div>
              <div>
                <div className="text-xs text-gray-400 mb-1">공식 여부</div>
                <span
                  className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                    item.isOfficial
                      ? 'bg-brand-100 text-brand-800'
                      : 'bg-gray-100 text-gray-500'
                  }`}
                >
                  {item.isOfficial ? '공식' : '비공식'}
                </span>
              </div>
            </div>
          </section>

          {/* 사용 통계 */}
          <section>
            <h3 className="text-sm font-medium text-gray-500 mb-3">사용 통계</h3>
            <div className="bg-gray-50 rounded-card p-4">
              <div className="text-2xl font-bold text-gray-900">{item.usageCount}회</div>
              <div className="text-sm text-gray-500">사용됨</div>
            </div>
          </section>

          {/* 시간 정보 */}
          <section>
            <h3 className="text-sm font-medium text-gray-500 mb-3">시간 정보</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <div className="text-xs text-gray-400 mb-1">생성일시</div>
                <div className="text-gray-600">
                  {new Date(item.createdAt).toLocaleString('ko-KR')}
                </div>
              </div>
              <div>
                <div className="text-xs text-gray-400 mb-1">수정일시</div>
                <div className="text-gray-600">
                  {new Date(item.updatedAt).toLocaleString('ko-KR')}
                </div>
              </div>
            </div>
          </section>
        </div>
    </Modal>
  );
}
